/**
 * TerminalTile — Plan E E.1+E.2+E.3 component (Pivot C + E.8 refactor)
 *
 * Single xterm.js terminal tile wrapping a WebSocket connection to a tenant
 * container's ttyd endpoint. Implements the ttyd 1.7.7 protocol verbatim
 * (verified upstream `tsl0922/ttyd@1.7.7/html/src/components/terminal/xterm/index.ts`):
 *
 *   Server → client (incoming):
 *     OUTPUT           = '0' — pty output, decoded as UTF-8 and written to terminal
 *     SET_WINDOW_TITLE = '1' — pty title (Phase 1 no-op; Phase 2 surfaces in tile header)
 *     SET_PREFERENCES  = '2' — JSON terminal options (parsed, malformed JSON ignored)
 *
 *   Client → server (outgoing):
 *     INPUT            = '0' — keystrokes from xterm.onData
 *     RESIZE_TERMINAL  = '1' — JSON {columns, rows} on open + window resize (MANDATORY:
 *                              without this ttyd allocates a default 80×24 pty regardless
 *                              of viewport — see Plan E E.0.d note)
 *     PAUSE / RESUME   = '2' / '3' — flow control (Phase 2)
 *     JSON_DATA        = '{' — auth frame `{"AuthToken":""}`. MANDATORY before any
 *                              RESIZE/INPUT, even when ttyd was started without `-c`.
 *
 * E.3 lifecycle: auto-reconnect on close (code 1006) with exponential backoff
 * (1s/2s/4s/8s/16s, MAX_RETRIES=5). After 5 failed retries, status flips to
 * 'failed' and scheduling stops. Manual Reconnect button clears any pending
 * timeout, resets the retry counter, and immediately attempts a new connection
 * — both initial and manual paths route through the same setupSocketHandlers
 * helper, so the auth+resize handshake and onclose retry logic exist in one
 * place (E.8 refactor closing E.G4-C2: pre-refactor, the manual path was a
 * 90-LOC duplicate of the initial path's connect() closure).
 *
 * Auth model (D1-5, 2026-06-09 audit — supersedes the original Plan E §0 note):
 * vibe-designer is Mode-1 OIDC PKCE since Workstream A.10 (2026-04-27) removed
 * Traefik ForwardAuth. The old claim "the WS inherits the vibe-{tenant} session
 * cookie" is FALSE post-A.10 — there is no cookie gate and the Mode-1 VPS nginx
 * has no auth_request, so the same-origin /api/ttyd-proxy/{service} WS reached
 * interactive ttyd SHELLS unauthenticated. The SPA now sends an in-band JWT auth
 * frame (`wsAuthFrame()` → `{type:"auth", token}`) as its FIRST WS frame; the
 * backend validates it (middleware.auth.consume_ws_auth_frame) before connecting
 * upstream. A 1008 close = auth rejection → the tile fails CLOSED (no reconnect).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { SerializeAddon } from '@xterm/addon-serialize';
import '@xterm/xterm/css/xterm.css';
import { useBroadcastStore, fanOut } from '../../stores/broadcastStore';
import { wsAuthFrame } from '../../services/api';
import { isShiftEnterKeydown, SHIFT_ENTER_BYTES } from './terminalKeyHandler';
import { decrqmPrivateResponse } from './terminalDecrqm';
import {
  loadSerializedBuffer,
  saveSerializedBuffer,
  clearSerializedBuffer,
} from './terminalSerialize';

export interface TerminalTileProps {
  url: string;       // ws[s]://{page-host}/api/ttyd-proxy/{service}[?instance=...] — same-origin Pivot C path
  label: string;     // tile header text (display_name overlay if set, else NATO name)
  onConnect?: () => void;
  onDisconnect?: (code: number, reason: string) => void;
  onError?: (e: Event) => void;
  onClose?: () => void; // Y.1a — when provided, header renders an "x" button that calls this
  // Y.3 — when provided, label becomes click-to-rename. Submit non-empty string
  // to set display_name; submit empty string to clear (returns to NATO name).
  // Errors (collision 409) surfaced via paneStore.error; tile UI doesn't catch.
  onRename?: (newDisplayName: string | null) => void;
}

// ttyd 1.7.7 protocol — VERBATIM string values from upstream Command enum
// (tsl0922/ttyd@1.7.7/html/src/components/terminal/xterm/index.ts).
// Incoming dispatch compares String.fromCharCode(firstByte) to these literals,
// so a wire-format change from text-prefix to numeric-prefix would surface
// immediately rather than silently passing because of ASCII coincidence.
const Cmd = {
  // Server → client (incoming)
  OUTPUT: '0',
  SET_WINDOW_TITLE: '1',
  SET_PREFERENCES: '2',
  // Client → server (outgoing)
  INPUT: '0',
  RESIZE_TERMINAL: '1',
  PAUSE: '2',
  RESUME: '3',
  // JSON_DATA: leading '{' (the entire frame is a JSON object whose first
  // char is the cmd byte). Used to send AuthToken — MANDATORY first frame
  // even when ttyd is started without `-c username:password`. Without it,
  // ttyd accepts the WS but never starts the pty (verified live 2026-04-26).
} as const;

// ttyd 1.7.7 mandates this WebSocket subprotocol on the upgrade. Without it,
// ttyd accepts the connection but never starts the pty.
const TTYD_SUBPROTOCOL = 'tty';

// D1-5 (2026-06-09 audit) — RFC 6455 policy-violation close code. The backend
// ttyd-proxy gate sends this when the in-band JWT auth frame is missing/invalid.
// Mirrors backend middleware.auth.WS_CLOSE_POLICY_VIOLATION. On this code the
// tile fails CLOSED (no reconnect) — retrying would loop against the gate.
const WS_CLOSE_POLICY_VIOLATION = 1008;

// Mandatory auth frame sent immediately on open. The leading `{` is both the
// first character of the JSON object AND the JSON_DATA cmd byte.
const TTYD_AUTH_FRAME = '{"AuthToken":""}';

// Workstream D.2: whitelist of ttyd-supplied prefs that are safe to apply to
// xterm options at runtime. Theme is included — values are CSS color objects
// rendered to canvas, no DOM injection risk. Other ttyd `-t` flags (e.g.
// disableLeaveAlert) intentionally excluded — not safe to apply blindly
// without per-pref validation.
const APPLY_TTYD_PREFS = new Set(['fontSize', 'fontFamily', 'cursorBlink', 'scrollback', 'theme']);

// E.3 reconnect tuning. Exponential backoff capped at 16s; gives up after
// MAX_RETRIES failures. Manual Reconnect resets the counter.
const MAX_RETRIES = 5;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 16000;

type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'failed';

const statusColor: Record<ConnectionStatus, string> = {
  connecting: 'bg-yellow-500',
  open: 'bg-green-500',
  reconnecting: 'bg-yellow-500',
  failed: 'bg-red-500',
};

export function TerminalTile({ url, label, onConnect, onDisconnect, onError, onClose, onRename }: TerminalTileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const serializeRef = useRef<SerializeAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const unmountedRef = useRef(false);
  // Fresh-on-reconnect: distinguishes initial mount (preserve page-reload
  // restore) from later opens (clear stale scrollback from prior PTY).
  const hasOpenedBeforeRef = useRef(false);
  // E.G2 reviewer C1: term.onData() returns an IDisposable that must be disposed
  // before re-registering, otherwise reconnects accumulate handlers and every
  // keystroke fires N times into the WS.
  const onDataDisposableRef = useRef<{ dispose: () => void } | null>(null);

  // Refs for label + parent callbacks so async handlers always read latest
  // values without forcing the WS to tear down + reopen on parent re-render.
  // E.G1 reviewer Concern 1 (label) was solved this way; E.8 extends the
  // pattern to onConnect/onDisconnect/onError, which closes the E.G1 C2 TODO.
  const labelRef = useRef(label);
  labelRef.current = label;
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);
  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;
  onErrorRef.current = onError;

  // Y.3 — inline rename UI. Click label → editing=true → input visible.
  // Enter submits via onRename (empty string clears via null). Esc cancels.
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const isRenaming = renameDraft !== null;

  const submitRename = () => {
    if (renameDraft === null) return;
    const trimmed = renameDraft.trim();
    onRename?.(trimmed === '' ? null : trimmed);
    setRenameDraft(null);
  };

  const cancelRename = () => setRenameDraft(null);

  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  // Workstream D.1: ttyd-supplied window title surfaced in tile header (falls
  // back to `label` prop when null). Updated on SET_WINDOW_TITLE messages.
  const [dynamicTitle, setDynamicTitle] = useState<string | null>(null);

  // Workstream D.3: session record/replay MVP — buffer captures decoded OUTPUT
  // payloads while `recording` is true; Replay button writes buffer back to
  // the same xterm via clear() + write(buffer). Starting a new recording
  // clears the buffer (overwrites previous). Replay disabled when empty.
  // Out-of-scope (Phase 3+): timing-accurate replay, cross-session persistence,
  // multi-tile broadcast, buffer size cap. See worldview §1.3.
  const [recordedSession, setRecordedSession] = useState<{ buffer: string; recording: boolean }>({
    buffer: '',
    recording: false,
  });
  // Ref mirror so the OUTPUT closure inside `setupSocketHandlers` (line ~157,
  // useCallback memoized with `[url, sendResize]`) reads the LATEST recording
  // state, not a stale closure-bound value. Refs are assigned during render —
  // placement HERE (after state declarations, BEFORE setupSocketHandlers
  // memoization) means the ref is current by the time the closure executes
  // on each render. Pattern matches `labelRef`/`onConnectRef` above.
  const recordingRef = useRef(false);
  recordingRef.current = recordedSession.recording;

  const handleToggleRecord = () => {
    setRecordedSession((prev) => {
      if (prev.recording) {
        // Stopping — preserve buffer for replay.
        return { ...prev, recording: false };
      }
      // Starting — clear buffer for fresh recording.
      return { buffer: '', recording: true };
    });
  };

  const handleReplay = () => {
    const term = termRef.current;
    if (!term) return;
    term.clear();
    term.write(recordedSession.buffer);
  };

  // E.4 broadcast — tile id is the url (unique per tile in any TerminalGrid).
  // Subscribe to broadcast.members.has(url) so the broadcast button reflects state.
  const isBroadcastMember = useBroadcastStore((s) => s.members.has(url));
  const toggleBroadcastMember = useBroadcastStore((s) => s.toggleMember);

  // Send RESIZE_TERMINAL — MANDATORY per E.0.d. Without this, ttyd allocates a
  // default 80×24 terminal regardless of actual viewport size.
  const sendResize = useCallback((ws: WebSocket) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    const fit = fitRef.current;
    if (!fit) return;
    const dims = fit.proposeDimensions();
    if (!dims) return;
    ws.send(Cmd.RESIZE_TERMINAL + JSON.stringify({ columns: dims.cols, rows: dims.rows }));
  }, []);

  // Ref bridge so `setupSocketHandlers`'s `ws.onclose` always reads the
  // latest memoized `scheduleReconnect` — keeping `scheduleReconnect` out of
  // setupSocketHandlers' dep list avoids re-memoizing on every retry-counter
  // tick (which would tear down the WS via useEffect's `[url, connect,
  // sendResize]` watch). `connect` does not need a similar ref because
  // `scheduleReconnect` already takes it as a useCallback dep — the
  // setTimeout closure inside scheduleReconnect captures the latest `connect`
  // directly.
  const scheduleReconnectRef = useRef<() => void>(() => {});

  // Wire all 5 handlers onto a fresh WS. Used by both the initial connect()
  // path and handleManualReconnect — single source of truth for the ttyd
  // handshake (auth → resize) and the canonical onclose → scheduleReconnect
  // path. Pre-E.8 this was duplicated across both paths.
  const setupSocketHandlers = useCallback((ws: WebSocket) => {
    const decoder = new TextDecoder();

    ws.onopen = () => {
      if (hasOpenedBeforeRef.current) {
        termRef.current?.clear();
        clearSerializedBuffer(url);
      }
      hasOpenedBeforeRef.current = true;
      // Successful connect — reset retry counter so future failures start fresh.
      retryCountRef.current = 0;
      setStatus('open');
      // D1-5 — JWT auth gate frame FIRST. The backend ttyd-proxy consumes this
      // frame and does NOT forward it to ttyd. Sent only when a token is stored
      // (AUTH_ENABLED tenants); null in local dev where the backend gate is also
      // skipped, so the ttyd JSON_DATA frame stays frame[0] there.
      const authFrame = wsAuthFrame();
      if (authFrame) {
        ws.send(JSON.stringify(authFrame));
      }
      // ttyd JSON_DATA auth frame MUST go before any RESIZE_TERMINAL/INPUT.
      ws.send(TTYD_AUTH_FRAME);
      sendResize(ws);
      // E.4: register this tile's WS in the broadcast store for fan-out routing.
      useBroadcastStore.getState().registerSocket(url, ws);
      onConnectRef.current?.();
    };

    ws.onmessage = (ev) => {
      if (!(ev.data instanceof ArrayBuffer)) return;
      const u8 = new Uint8Array(ev.data);
      if (u8.length === 0) return;
      const cmd = String.fromCharCode(u8[0]);
      const payload = u8.subarray(1);
      const term = termRef.current;
      switch (cmd) {
        case Cmd.OUTPUT: {
          const decoded = decoder.decode(payload);
          term?.write(decoded);
          // Workstream D.3: append to session buffer when recording.
          if (recordingRef.current) {
            setRecordedSession((prev) => ({ ...prev, buffer: prev.buffer + decoded }));
          }
          break;
        }
        case Cmd.SET_WINDOW_TITLE: {
          // Workstream D.1: surface ttyd-supplied title in tile header.
          // Empty/whitespace-only payload → fall back to static `label` prop.
          const incoming = decoder.decode(payload).trim();
          setDynamicTitle(incoming || null);
          break;
        }
        case Cmd.SET_PREFERENCES: {
          // Workstream D.2: apply whitelisted ttyd-supplied prefs to xterm
          // options. Unknown/non-whitelisted keys ignored (defensive — we
          // don't fully validate server-supplied data beyond the whitelist).
          // xterm 6.x: `term.options` is a writable ITerminalOptions per
          // node_modules/@xterm/xterm/typings/xterm.d.ts (verified NH.D.G0).
          try {
            const prefs = JSON.parse(decoder.decode(payload));
            if (term && prefs && typeof prefs === 'object') {
              for (const [key, value] of Object.entries(prefs)) {
                if (APPLY_TTYD_PREFS.has(key)) {
                  (term.options as Record<string, unknown>)[key] = value;
                }
              }
            }
          } catch {
            /* malformed prefs from server — ignore (upstream wire-format error) */
          }
          break;
        }
        default:
          // Unknown command byte — log for debugging (silent ignore would mask protocol drift).
          console.warn(`[TerminalTile ${labelRef.current}] unknown ttyd cmd: 0x${u8[0].toString(16)}`);
      }
    };

    ws.onerror = (e) => {
      // Unconditional log — silent WS failures in a multi-tile grid are debug-impossible.
      console.error(`[TerminalTile ${labelRef.current}] WS error:`, e);
      onErrorRef.current?.(e);
    };

    ws.onclose = (e) => {
      onDisconnectRef.current?.(e.code, e.reason);
      // D1-5 — auth rejection (1008) fails CLOSED: reconnecting would loop
      // forever against the gate. Every other close code keeps the canonical
      // exponential-backoff retry (1006 transient drop, etc.).
      if (e.code === WS_CLOSE_POLICY_VIOLATION) {
        setStatus('failed');
        return;
      }
      // Auto-reconnect via the canonical retry path (initial + manual unify here).
      scheduleReconnectRef.current();
    };

    // E.G2 reviewer C1: dispose any previous onData handler before re-registering.
    onDataDisposableRef.current?.dispose?.();
    const term = termRef.current;
    if (term) {
      onDataDisposableRef.current = term.onData((data) => {
        // ttyd protocol: client → server input is `'0' + data` (Cmd.INPUT)
        const payload = new TextEncoder().encode(Cmd.INPUT + data);
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(payload);
        }
        // E.4 broadcast fan-out (echo-skip — fanOut() never sends to selfId).
        // Always called; fanOut() short-circuits when disabled or self not in members.
        fanOut(url, payload);
      });
    }
  }, [url, sendResize]);

  const connect = useCallback(() => {
    if (unmountedRef.current) return;
    setStatus('connecting');
    const ws = new WebSocket(url, [TTYD_SUBPROTOCOL]);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    setupSocketHandlers(ws);
  }, [url, setupSocketHandlers]);

  const scheduleReconnect = useCallback(() => {
    if (unmountedRef.current) return;
    if (retryCountRef.current >= MAX_RETRIES) {
      setStatus('failed');
      return;
    }
    const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, retryCountRef.current), BACKOFF_CAP_MS);
    retryCountRef.current += 1;
    setStatus('reconnecting');
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  // Sync the bridge ref every render so onclose closures always see the
  // latest memoized callback. Under React StrictMode double-render in dev,
  // assigning the same value twice is a no-op for refs — not a race.
  scheduleReconnectRef.current = scheduleReconnect;

  useEffect(() => {
    if (!containerRef.current) return;

    unmountedRef.current = false;

    const term = new Terminal({ fontSize: 14, theme: { background: '#1a1a1a' } });
    const fit = new FitAddon();
    const serialize = new SerializeAddon();
    term.loadAddon(fit);
    term.loadAddon(serialize);
    term.open(containerRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;
    serializeRef.current = serialize;

    // Custom parser handlers registered on this terminal; disposed on unmount.
    const parserDisposables: { dispose: () => void }[] = [];

    // OSC 10/11/12 dynamic-color queries: when server sends `ESC ]11;?BEL`
    // (background color query), xterm.js's default handler emits a response
    // via the input callback — which term.onData at :315 then ships to ttyd
    // as a TTYD_INPUT frame. hermes (Python TUI) echoes the response as
    // literal text. Returning true suppresses the default response.
    [10, 11, 12].forEach((ident) => {
      parserDisposables.push(term.parser.registerOscHandler(ident, () => true));
    });

    // Private DECRQM (`ESC [ ? <mode> $ p`): opencode's TUI (opentui) probes
    // private DEC modes (2026 sync-output, 2004, 1004, 1016, 2027, 2031 …) at
    // startup. xterm 6.0.0's built-in `requestMode` answers them but THROWS
    // `ReferenceError: n is not defined` in our re-minified bundle, aborting
    // `_innerWrite` so the whole alt-screen paint is dropped and the pane
    // freezes. Answer DECRQM ourselves and return true so the broken built-in
    // never runs. See terminalDecrqm.ts for the full root-cause writeup.
    parserDisposables.push(
      term.parser.registerCsiHandler({ prefix: '?', intermediates: '$', final: 'p' }, (params) => {
        const raw = params[0];
        const mode = Array.isArray(raw) ? raw[0] : raw;
        const reply = decrqmPrivateResponse(mode as number);
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          // DECRPM is an automatic per-pty protocol reply, NOT a user
          // keystroke — send it only to THIS pane's upstream, never fanOut to
          // broadcast siblings (each pane answers its own pty's DECRQM).
          ws.send(new TextEncoder().encode(Cmd.INPUT + reply));
        }
        return true;
      }),
    );

    // E.21.24: Shift+Enter -> LF (newline-without-submit). Must run BEFORE
    // xterm's default handling so we can suppress its `\r` emission and
    // write `\n` instead. Returning false from the custom handler short-
    // circuits xterm's default character/sequence generation.
    term.attachCustomKeyEventHandler((event) => {
      if (isShiftEnterKeydown(event)) {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          const payload = new TextEncoder().encode(Cmd.INPUT + SHIFT_ENTER_BYTES);
          ws.send(payload);
          fanOut(url, payload);
        }
        return false; // suppress xterm's default `\r` emission
      }
      return true;
    });

    // E.21.24: restore prior buffer snapshot from localStorage if any.
    // This is VISUAL state only — the underlying PTY is fresh after a page
    // reload because the WS close terminates ttyd's PTY upstream.
    const snapshot = loadSerializedBuffer(url);
    if (snapshot) {
      term.write(snapshot);
    }

    // Window resize → re-fit + send RESIZE_TERMINAL to current ws
    const handleResize = () => {
      fit.fit();
      const ws = wsRef.current;
      if (ws) sendResize(ws);
    };
    window.addEventListener('resize', handleResize);

    // E.21.10: panel-handle drags don't fire window 'resize'. ResizeObserver
    // fires whenever the tile container changes size (parent flex change,
    // PanelGroup resize handle, etc.) so xterm refits to new dimensions.
    const ro = new ResizeObserver(handleResize);
    if (containerRef.current) ro.observe(containerRef.current);

    // E.21.24: persist xterm buffer on full-page unload so the next mount
    // can restore visual scrollback. (Tab toggles are already covered by
    // DualShellLayout — this only fires on browser refresh / nav-away.)
    //
    // The serialize() call can throw if the addon isn't fully wired (e.g.
    // mid-teardown, or under test where loadAddon is mocked) — best-effort.
    const tryPersistBuffer = () => {
      const s = serializeRef.current;
      if (!s) return;
      try {
        saveSerializedBuffer(url, s.serialize());
      } catch {
        // Addon not loaded / terminal disposed — skip; non-critical.
      }
    };
    window.addEventListener('beforeunload', tryPersistBuffer);

    connect();

    return () => {
      unmountedRef.current = true;
      // Save BEFORE disposing the terminal so the snapshot is captured.
      tryPersistBuffer();
      window.removeEventListener('beforeunload', tryPersistBuffer);
      window.removeEventListener('resize', handleResize);
      ro.disconnect();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // E.G2 reviewer C1: dispose the active onData handler before disposing the terminal.
      onDataDisposableRef.current?.dispose?.();
      onDataDisposableRef.current = null;
      wsRef.current?.close();
      // E.4: remove this tile from the broadcast registry on unmount.
      useBroadcastStore.getState().unregisterSocket(url);
      serializeRef.current = null;
      parserDisposables.forEach((d) => d.dispose());
      term.dispose();
    };
    // url + connect + sendResize are the only true deps. onConnect/onDisconnect/
    // onError travel via refs above so a parent's inline-lambda re-render
    // doesn't tear the WS down.
  }, [url, connect, sendResize]);

  const handleManualReconnect = () => {
    // Cancel any pending scheduled reconnect.
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    // Reset retry counter so backoff restarts from 1s on the next failure.
    retryCountRef.current = 0;
    // Tear down current ws (if any) — detach onclose first to suppress the
    // canonical auto-reconnect path; we're starting a fresh manual cycle.
    const oldWs = wsRef.current;
    if (oldWs && oldWs.readyState !== WebSocket.CLOSED) {
      oldWs.onclose = null;
      oldWs.close();
    }
    // Single source of truth: connect() routes through setupSocketHandlers
    // (auth+resize) and onclose → scheduleReconnect (canonical retry path).
    connect();
  };

  const showReconnect = status === 'reconnecting' || status === 'failed';

  return (
    <div className="flex flex-col h-full bg-vibe-bg-secondary border border-vibe-border rounded">
      <div className="flex items-center justify-between px-3 py-2 border-b border-vibe-border text-xs">
        <div className="flex items-center gap-2">
          <span
            data-testid="terminal-status"
            data-status={status}
            className={`w-2 h-2 rounded-full ${statusColor[status]}`}
            aria-label={`status: ${status}`}
          />
          {isRenaming ? (
            <input
              type="text"
              autoFocus
              value={renameDraft ?? ''}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitRename();
                else if (e.key === 'Escape') cancelRename();
              }}
              onBlur={cancelRename}
              data-testid="rename-input"
              aria-label={`rename pane ${label}`}
              className="px-1 py-0 bg-vibe-bg border border-vibe-accent rounded text-xs"
            />
          ) : onRename ? (
            <button
              type="button"
              onClick={() => setRenameDraft(dynamicTitle ?? label)}
              data-testid="rename-label"
              aria-label={`rename pane ${label}`}
              className="hover:underline cursor-text text-left"
            >
              {dynamicTitle ?? label}
            </button>
          ) : (
            <span>{dynamicTitle ?? label}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => toggleBroadcastMember(url)}
            aria-pressed={isBroadcastMember}
            aria-label={`broadcast ${isBroadcastMember ? 'on' : 'off'} for ${label}`}
            className={`px-2 py-0.5 text-xs rounded border ${
              isBroadcastMember
                ? 'border-vibe-accent bg-vibe-accent text-white'
                : 'border-vibe-border hover:bg-vibe-bg'
            }`}
          >
            Broadcast
          </button>
          {/* Workstream D.3: Record + Replay buttons — MVP buffer-only same-tile */}
          <button
            type="button"
            onClick={handleToggleRecord}
            aria-pressed={recordedSession.recording}
            aria-label={`record ${recordedSession.recording ? 'on' : 'off'} for ${dynamicTitle ?? label}`}
            className={`px-2 py-0.5 text-xs rounded border ${
              recordedSession.recording
                ? 'border-red-500 bg-red-500 text-white'
                : 'border-vibe-border hover:bg-vibe-bg'
            }`}
          >
            {recordedSession.recording ? 'Recording…' : 'Record'}
          </button>
          <button
            type="button"
            onClick={handleReplay}
            disabled={!recordedSession.buffer}
            aria-label={`replay for ${dynamicTitle ?? label}`}
            className="px-2 py-0.5 text-xs rounded border border-vibe-border hover:bg-vibe-bg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Replay
          </button>
          {showReconnect && (
            <button
              type="button"
              onClick={handleManualReconnect}
              className="px-2 py-0.5 text-xs rounded border border-vibe-border hover:bg-vibe-bg"
            >
              Reconnect
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={`close pane ${label}`}
              data-testid="pane-close-button"
              className="px-2 py-0.5 text-xs rounded border border-vibe-border hover:bg-red-500 hover:text-white hover:border-red-500"
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div ref={containerRef} className="flex-1" />
    </div>
  );
}
